<template>
    <div>
        <h6 class="text-overline font-weight-bold">
            {{ $translate("productMetadata") }}
        </h6>

        <v-row class="mt-1">
            <v-col cols="12" sm="4" class="pt-2">
                <v-combobox
                    dense
                    :value="activeProducttypeName"
                    item-value="value"
                    item-text="text"
                    @change="onProducttypeChange"
                    :label="$translate('productType')"
                    :clearable="isCustomer"
                    :disabled="!isCustomer"
                    :error-messages="productSavingErrors.producttype"
                    :items="producttypesChoices"
                    :outlined="outlined"
                    hide-details="auto"
                    />
            </v-col>
            <template v-if="product?.producttype">
                <v-col
                    cols="6"
                    sm="4"
                    class="pt-2"
                    v-for="field in getProductTypeFields()"
                    :key="field.code"
                >
                    <Select
                        v-if="field.type == 'choices'"
                        :value="product.metadata[field.code]"
                        :outlined="outlined"
                        dense
                        @input="
                            $emit('metadata-changed', {
                                code: field.code,
                                value: $event,
                            })
                        "
                        :label="fieldLabel(field)"
                        :class="field.required ? 'font-weight-bold' : null"
                        :clearable="isCustomer"
                        :disabled="!isCustomer || isFieldDisabled(field.code)"
                        :required="field.required"
                        :error-messages="
                            productSavingErrors.metadata
                                ? productSavingErrors.metadata[field.code]
                                : null
                        "
                        :options="field | fieldOptions"
                        hide-details="auto"
                    />
                    <v-select
                        v-else-if="field.type == 'boolean'"
                        :value="product.metadata[field.code]"
                        @input="
                            $emit('metadata-changed', {
                                code: field.code,
                                value: $event,
                            })
                        "
                        :outlined="outlined"
                        dense
                        hide-details="auto"
                        :label="fieldLabel(field)"
                        :clearable="isCustomer"
                        :disabled="!isCustomer || isFieldDisabled(field.code)"
                        :required="field.required"
                        :error-messages="
                            productSavingErrors.metadata
                                ? productSavingErrors.metadata[field.code]
                                : null
                        "
                        :items="[
                            { text: $translate('yes'), value: true },
                            { text: $translate('no'), value: false },
                        ]"
                    />
                    <v-text-field
                        v-else
                        :value="product.metadata[field.code]"
                        @input="
                            $emit('metadata-changed', {
                                code: field.code,
                                value: $event,
                            })
                        "
                        @keydown="$emit('keydown', $event)"
                        :outlined="outlined"
                        hide-details="auto"
                        dense
                        :class="field.required ? 'font-weight-bold' : null"
                        :label="fieldLabel(field)"
                        :clearable="isCustomer"
                        :disabled="!isCustomer || isFieldDisabled(field.code)"
                        :required="field.required"
                        :format="fieldFormatForField(field)"
                        :type="fieldTypeForField(field)"
                        :error-messages="
                            productSavingErrors.metadata
                                ? productSavingErrors.metadata[field.code]
                                : null
                        "
                    />
                </v-col>
            </template>
        </v-row>
    </div>
</template>

<script>
import GlobalMixins from "@/mixins/global";

export default {
    mixins: [GlobalMixins],
    props: {
        product: {
            required: true,
            type: Object,
        },
        producttypes: {
            required: true,
            type: Array,
        },
        productSavingErrors: {
            required: true,
            type: Object,
        },
        outlined: {
            required: false,
            type: Boolean,
            default: false,
        },
        disabledCodes: {
            type: Array,
            default: () => [],
        },
    },
    filters: {
        fieldOptions(field) {
            if (!field || !field.choices) {
                return [];
            }
            return field.choices.map((item) => {
                return {
                    text: item,
                    value: item,
                };
            });
        },
    },
    methods: {
        /**
         * Combobox clear yields null/'' — emit explicit null so the save payload
         * includes `"producttype": null` (JSON omits `undefined`).
         * @param {null|string|{ value?: number|string|null }|undefined} event
         */
        onProducttypeChange(event) {
            if (event == null || event === "") {
                this.$emit("producttype-changed", null);
                return;
            }
            if (typeof event === "object" && "value" in event) {
                this.$emit(
                    "producttype-changed",
                    event.value == null || event.value === ""
                        ? null
                        : event.value
                );
                return;
            }
            this.$emit("producttype-changed", event);
        },
        isFieldDisabled(code) {
            return (this.disabledCodes || []).includes(code);
        },
        getProductTypeFields() {
            if (!this.product.producttype) {
                return [];
            }
            if (!this.producttypes || !this.producttypes.length) {
                return [];
            }
            let producttype = this.producttypes.find(
                (item) => this.product.producttype == item.id
            );
            if (!producttype?.fields) {
                return [];
            }
            let required_fields = producttype.fields.filter(
                (item) => item.required
            );
            let optional_fields = producttype.fields
                .filter((item) => !item.required)
                .sort((a, b) => a.label.localeCompare(b.label));
            return required_fields.concat(optional_fields);
        },
        fieldTypeForField(field) {
            switch (field.type) {
                case "choices":
                    return "text";
                case "integer":
                    return "number";
                case "float":
                    return "float";
                default:
                    return "text";
            }
        },
        fieldFormatForField(field) {
            switch (field.type) {
                case "choices":
                    return "string";
                case "integer":
                    return "integer";
                case "float":
                    return "float";
                default:
                    return "string";
            }
        },
        fieldLabel(field) {
            let label = field.label;

            if (field.unit) {
                label += ` (${field.unit})`;
            }
            if (field.required) {
                label += ` - ${this.$translate("required")}`;
            }

            return label;
        },
    },
    computed: {
        activeProducttypeName() {
            return (this.producttypes || []).find(
                (item) => item.id == this.product.producttype
            )?.label;
        },
        producttypesChoices() {
            const choices = (this.producttypes || []).map((item) => {
                return {
                    text: item.label,
                    value: item.id,
                };
            });
            if (
                this.isCustomer &&
                this.userHasPermission("core.add_producttype")
            ) {
                choices.push({
                    text: this.$translate("createProductType"),
                    value: "new",
                });
            }
            return choices;
        },
        producttype() {
            return (this.producttypes || []).find(
                (item) => item.id == this.product.producttype
            );
        },
    },
    i18n: {
        messages: {
            en: {
                productMetadata: "Product metadata",
                productType: "Product type",
                createProductType: "Create a product type…",
                yes: "Yes",
                no: "No",
                required: "required",
            },
            fr: {
                productMetadata: "Métadonnées du produit",
                productType: "Type de produit",
                createProductType: "Créer un type de produit…",
                yes: "Oui",
                no: "Non",
                required: "requis",
            },
        },
    },
};
</script>
